/**
 * Detects the app's own blocking loading screens.
 *
 * The pipeline throws up full-screen overlays ("Analyzing design", "Detecting
 * colors") that swallow every click. If the tutorial keeps advancing behind
 * one, the learner surfaces from the loading screen part-way through a step
 * they never saw, having been told to click something that wasn't clickable.
 * Overlays carry `data-tutorial-busy` so the walkthrough can hold its place.
 */

const BUSY_SELECTOR = '[data-tutorial-busy]';

/** True while a blocking loading overlay is on screen. */
export const isAppBusy = () => {
  const nodes = document.querySelectorAll(BUSY_SELECTOR);

  for (const node of nodes) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      continue;
    }
    return true;
  }

  return false;
};

/**
 * Runs `callback` as soon as the app is idle, polling until then.
 * Returns a cancel function.
 */
export const whenAppIdle = (callback, { interval = 300, timeout = 120000 } = {}) => {
  let elapsed = 0;
  let timer = null;
  let cancelled = false;

  const tick = () => {
    if (cancelled) return;

    if (!isAppBusy()) {
      callback();
      return;
    }

    if (elapsed >= timeout) {
      console.warn('⚠️ Tutorial: app stayed busy for too long, continuing anyway');
      callback();
      return;
    }

    elapsed += interval;
    timer = setTimeout(tick, interval);
  };

  tick();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
};
